import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Car, Zap, ShieldCheck, Truck, Check, Plus, Clock, Loader2, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VEHICLE_ICONS = { sedan: Car, sports: Zap, suv: ShieldCheck, truck: Truck };

const reveal = {
  hidden: { opacity: 0, y: 48 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

function OptionCard({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={`relative w-full rounded-2xl border p-5 text-left transition-[border-color,background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 ${
        active
          ? "border-[#FFB703]/60 bg-[#FFB703]/[0.07] shadow-[0_0_44px_rgba(255,183,3,0.10)]"
          : "border-white/10 bg-[#12141C]/80 hover:border-white/25"
      }`}
    >
      <span
        className={`absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border transition-[background-color,border-color] duration-300 ${
          active ? "border-[#FFB703] bg-[#FFB703] text-[#090A0E]" : "border-white/20 text-transparent"
        }`}
      >
        <Check className="h-3 w-3" strokeWidth={3} />
      </span>
      {children}
    </button>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0C0E14] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#FFB703]/60 focus:shadow-[0_0_0_3px_rgba(255,183,3,0.12)]";

export default function Estimator() {
  const [pricing, setPricing] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [vehicle, setVehicle] = useState("sedan");
  const [pkg, setPkg] = useState("full");
  const [condition, setCondition] = useState("moderate");
  const [extras, setExtras] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", vehicle_model: "", notes: "" });
  const [lead, setLead] = useState(null);
  const [deposit, setDeposit] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/pricing`)
      .then((r) => setPricing(r.data))
      .catch(() => setLoadError(true));
    axios.get(`${API}/payments/deposit`).then((r) => setDeposit(r.data)).catch(() => setDeposit(null));
  }, []);

  const quote = useMemo(() => {
    if (!pricing) return null;
    const vt = pricing.vehicle_types.find((v) => v.id === vehicle);
    const p = pricing.packages.find((x) => x.id === pkg);
    const c = pricing.conditions.find((x) => x.id === condition);
    const ex = pricing.extras.filter((e) => extras.includes(e.id));
    const adjusted = Math.round(p.base_price * vt.multiplier * c.multiplier);
    const extrasTotal = ex.reduce((s, e) => s + e.price, 0);
    return { vt, p, c, ex, adjusted, extrasTotal, total: adjusted + extrasTotal };
  }, [pricing, vehicle, pkg, condition, extras]);

  const toggleExtra = (id) => setExtras((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        preferred_date: form.preferred_date || null,
        vehicle: form.vehicle_model || null,
        notes: form.notes || null,
        selections: { vehicle_type: vehicle, package: pkg, condition, extras },
      });
      toast.success(`Blueprint locked — $${res.data.quote.total.toLocaleString()}`, {
        description: "Our master detailers will reply within 60 minutes.",
      });
      setLead(res.data);
      setForm({ name: "", email: "", phone: "", preferred_date: "", vehicle_model: "", notes: "" });
      if (!deposit) setDialogOpen(false);
    } catch {
      toast.error("Could not submit your quote. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: deposit.lookup_key,
        origin_url: window.location.origin,
        lead_id: lead.id,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setPaying(false);
    }
  };

  const closeDialog = (open) => {
    setDialogOpen(open);
    if (!open) setLead(null);
  };

  return (
    <section id="estimator" data-testid="estimator-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FFB703]/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mb-14 max-w-3xl">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-[#FFB703]">02 // The Configurator</p>
          <h2 className="font-display text-[clamp(1.4rem,6vw,2.25rem)] font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            Instant Quote <span className="text-[#FFB703]">Estimator</span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-slate-400 sm:text-base">
            Build your tailored detailing blueprint in real time. Full pricing transparency — zero hidden fees.
          </p>
        </motion.div>

        {loadError && (
          <p data-testid="estimator-load-error" className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
            Could not load pricing. Please refresh the page.
          </p>
        )}

        {pricing && quote && (
          <div className="grid gap-10 lg:grid-cols-[1fr_380px] lg:gap-14">
            <div className="flex flex-col gap-14">
              <motion.fieldset variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
                <legend className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                  <span className="text-[#FFB703]">Step 01</span> — Vehicle Category
                </legend>
                <div className="grid gap-4 sm:grid-cols-2">
                  {pricing.vehicle_types.map((v) => {
                    const Icon = VEHICLE_ICONS[v.id] || Car;
                    return (
                      <OptionCard key={v.id} testId={`estimator-vehicle-${v.id}`} active={vehicle === v.id} onClick={() => setVehicle(v.id)}>
                        <Icon className={`mb-3 h-6 w-6 transition-colors ${vehicle === v.id ? "text-[#FFB703]" : "text-slate-500"}`} />
                        <p className="font-display text-base font-bold text-white">{v.name}</p>
                        <p className="mt-1 text-xs text-slate-500">{v.blurb}</p>
                        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">×{v.multiplier.toFixed(2)} size factor</p>
                      </OptionCard>
                    );
                  })}
                </div>
              </motion.fieldset>

              <motion.fieldset variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
                <legend className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                  <span className="text-[#FFB703]">Step 02</span> — Detailing Package
                </legend>
                <div className="flex flex-col gap-4">
                  {pricing.packages.map((p) => (
                    <OptionCard key={p.id} testId={`estimator-package-${p.id}`} active={pkg === p.id} onClick={() => setPkg(p.id)}>
                      <div className="flex flex-wrap items-baseline justify-between gap-2 pr-8">
                        <p className="font-display text-base font-bold text-white sm:text-lg">{p.name}</p>
                        <p className={`font-mono text-sm font-bold ${pkg === p.id ? "text-[#FFB703]" : "text-slate-400"}`}>
                          from ${p.base_price.toLocaleString()}
                        </p>
                      </div>
                      <p className="mt-1.5 max-w-xl text-xs leading-relaxed text-slate-500 sm:text-sm">{p.blurb}</p>
                      <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                        <Clock className="h-3 w-3" /> {p.duration}
                      </p>
                    </OptionCard>
                  ))}
                </div>
              </motion.fieldset>

              <motion.fieldset variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
                <legend className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                  <span className="text-[#FFB703]">Step 03</span> — Current Surface Condition
                </legend>
                <div className="grid gap-4 sm:grid-cols-2">
                  {pricing.conditions.map((c) => (
                    <OptionCard key={c.id} testId={`estimator-condition-${c.id}`} active={condition === c.id} onClick={() => setCondition(c.id)}>
                      <p className="pr-8 font-display text-base font-bold text-white">{c.name}</p>
                      <p className="mt-1 text-xs text-slate-500">{c.blurb}</p>
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">×{c.multiplier.toFixed(2)} labor factor</p>
                    </OptionCard>
                  ))}
                </div>
              </motion.fieldset>

              <motion.fieldset variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
                <legend className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                  <span className="text-[#FFB703]">Step 04</span> — Bespoke Add-Ons
                </legend>
                <div className="grid gap-4 sm:grid-cols-2">
                  {pricing.extras.map((x) => {
                    const active = extras.includes(x.id);
                    return (
                      <OptionCard key={x.id} testId={`estimator-extra-${x.id}`} active={active} onClick={() => toggleExtra(x.id)}>
                        <div className="flex items-center gap-2 pr-8">
                          <Plus className={`h-4 w-4 transition-transform duration-300 ${active ? "rotate-45 text-[#FFB703]" : "text-slate-500"}`} />
                          <p className="font-display text-sm font-bold text-white sm:text-base">{x.name}</p>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{x.blurb}</p>
                        <p className={`mt-2 font-mono text-xs font-bold ${active ? "text-[#FFB703]" : "text-slate-400"}`}>
                          +${x.price.toLocaleString()}
                        </p>
                      </OptionCard>
                    );
                  })}
                </div>
              </motion.fieldset>
            </div>

            <aside className="lg:sticky lg:top-24 lg:self-start">
              <motion.div
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                data-testid="quote-summary-card"
                className="overflow-hidden rounded-2xl border border-white/10 bg-[#12141C]/90 shadow-[0_0_60px_rgba(0,0,0,0.5)] backdrop-blur-md"
              >
                <div className="border-b border-white/10 px-6 py-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Live Blueprint</p>
                </div>
                <div className="flex flex-col gap-3 px-6 py-5 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Package</span>
                    <span data-testid="summary-package" className="text-right font-medium text-white">{quote.p.name}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Vehicle</span>
                    <span data-testid="summary-vehicle" className="text-right text-slate-300">{quote.vt.name}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Condition</span>
                    <span data-testid="summary-condition" className="text-right text-slate-300">{quote.c.name}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Est. time</span>
                    <span data-testid="summary-duration" className="font-mono text-slate-300">{quote.p.duration}</span>
                  </div>
                </div>
                <div className="border-t border-dashed border-white/10 px-6 py-5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Adjusted package</span>
                    <span data-testid="summary-adjusted" className="font-mono text-slate-300">${quote.adjusted.toLocaleString()}</span>
                  </div>
                  <AnimatePresence>
                    {quote.ex.map((x) => (
                      <motion.div
                        key={x.id}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex justify-between overflow-hidden text-sm"
                      >
                        <span className="pt-2 text-slate-500">{x.name}</span>
                        <span className="pt-2 font-mono text-slate-300">+${x.price.toLocaleString()}</span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Estimated Total</span>
                    <motion.span
                      key={quote.total}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35 }}
                      data-testid="summary-total-price"
                      className="font-mono text-3xl font-extrabold text-[#FFB703] sm:text-4xl"
                    >
                      ${quote.total.toLocaleString()}
                    </motion.span>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <button
                    type="button"
                    data-testid="lock-in-quote-button"
                    onClick={() => setDialogOpen(true)}
                    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFB703] py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#FFA200] hover:shadow-[0_0_36px_rgba(255,183,3,0.4)]"
                  >
                    Lock In This Blueprint
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <p className="mt-3 text-center text-[11px] text-slate-600">Final quote confirmed after on-site inspection.</p>
                </div>
              </motion.div>
            </aside>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent data-testid="lead-dialog" className="border-white/10 bg-[#12141C] text-slate-200 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold uppercase tracking-tight text-white">
              Lock In Your <span className="text-[#FFB703]">Blueprint</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-400">
              {quote && (
                <>
                  {quote.p.name} · {quote.vt.name} — <span className="font-mono text-[#FFB703]">${quote.total.toLocaleString()}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {lead && deposit ? (
            <div data-testid="deposit-step" className="mt-2 flex flex-col gap-4">
              <div className="rounded-xl border border-[#FFB703]/30 bg-[#FFB703]/5 p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB703]">Request received</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Secure your spot now with a <span className="font-mono font-semibold text-white" data-testid="deposit-amount">${deposit.amount.toFixed(0)} {deposit.currency}</span> booking
                  deposit. It comes straight off your final price — the balance is payable on the day.
                </p>
              </div>
              <button
                type="button"
                onClick={payDeposit}
                disabled={paying}
                data-testid="pay-deposit-button"
                className="flex items-center justify-center gap-2 rounded-xl bg-[#FFB703] py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-[background-color,box-shadow] duration-300 hover:bg-[#FFA200] hover:shadow-[0_0_30px_rgba(255,183,3,0.35)] disabled:opacity-60"
              >
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay $${deposit.amount.toFixed(0)} Deposit — Secure Booking`}
              </button>
              <button
                type="button"
                onClick={() => closeDialog(false)}
                data-testid="skip-deposit-button"
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-white"
              >
                Maybe later — just send my request
              </button>
              <p className="text-center text-[11px] text-slate-600">Secure card payment powered by Stripe.</p>
            </div>
          ) : (
          <form onSubmit={submitLead} className="mt-2 flex flex-col gap-3">
            <input required data-testid="lead-input-name" placeholder="Full name *" value={form.name} onChange={setField("name")} className={inputCls} />
            <div className="grid gap-3 sm:grid-cols-2">
              <input required type="email" data-testid="lead-input-email" placeholder="Email *" value={form.email} onChange={setField("email")} className={inputCls} />
              <input data-testid="lead-input-phone" placeholder="Phone" value={form.phone} onChange={setField("phone")} className={inputCls} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="lead-input-date" placeholder="Preferred date (e.g. Sat 12 Jul)" value={form.preferred_date} onChange={setField("preferred_date")} className={inputCls} />
              <input data-testid="lead-input-vehicle" placeholder="Vehicle year / make / model" value={form.vehicle_model} onChange={setField("vehicle_model")} className={inputCls} />
            </div>
            <textarea data-testid="lead-input-notes" placeholder="Special requests, garage location…" rows={3} value={form.notes} onChange={setField("notes")} className={`${inputCls} resize-none`} />
            <button
              type="submit"
              disabled={submitting}
              data-testid="lead-submit-button"
              className="mt-1 flex items-center justify-center gap-2 rounded-xl bg-[#FFB703] py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-[background-color,box-shadow] duration-300 hover:bg-[#FFA200] hover:shadow-[0_0_30px_rgba(255,183,3,0.35)] disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit & Request Appointment"}
            </button>
          </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
